import dot from 'dot-prop-immutable';
import moment from 'moment';

import { PAGE_REQUEST } from 'store/app/actions';

import { getErrorMessage } from 'utils/stringUtils';

import * as types from './types';

const initialState = {
  emailNotificationSettings: [],
  isExportingInvoicesToCsv: false,
  isPOUpdating: false,
  isOrderQuoteCopying: false,
  changeOrderStatusFetching: false,
  orderId: null,
  createShipment: {
    ui: {
      isModalOpen: false,
      step: 1,
      isCreating: false,
      isSendInvoiceFetching: false,
      error: null,
      errorSource: null,
    },
    data: {
      orderItemToQuantityMapping: {},
      invoiceNumber: null,
      paymentDueDate: null,
      shipmentDate: moment(),
      additionalShippingCharge: null,
      trackingNumber: null,
      pickupRecipient: null,
      email: null,
    },
    emailTemplates: null,
    emailNotificationSettings: [],
  },
  shipments: [], // TODO
  downloadFiles: {
    isFetching: false,
  },
  cancelOrder: {
    isFetching: false,
  },
  refundNotification: {
    isModalOpen: false,
    isFetching: false,
    emailTemplates: [],
    error: null,
  },
  expandedQuoteItemId: null,
};

const reducer = (state = initialState, action) => {
  const { payload } = action;
  switch (action.type) {
    case PAGE_REQUEST:
      return {
        ...state,
      };
    case types.INIT_EDIT_ORDER:
      return {
        ...state,
        orderId: payload.result,
      };
    case types.ORDER_UPDATE_REQUEST:
      return { ...state, changeOrderStatusFetching: true };
    case types.ORDER_UPDATE_SUCCESS:
    case types.ORDER_UPDATE_ERROR:
      return { ...state, changeOrderStatusFetching: false };

    case types.CREATE_SHIPMENT_FLOW: {
      let nextState = state;
      nextState = dot.set(state, 'createShipment', initialState.createShipment);
      nextState = dot.set(nextState, 'createShipment.ui.isModalOpen', true);
      return nextState;
    }

    case types.CREATE_SHIPMENT_CLOSE:
      return dot.set(state, 'createShipment.ui.isModalOpen', false);

    case types.CREATE_SHIPMENT_SET_DATA:
      return dot.merge(state, 'createShipment.data', payload);

    case types.CREATE_SHIPMENT_NEXT:
      return dot.merge(state, 'createShipment.ui', {
        step: Math.min(3, state.createShipment.ui.step + 1),
        error: null,
      });

    case types.CREATE_SHIPMENT_BACK:
      return dot.merge(state, 'createShipment.ui', {
        step: Math.max(1, state.createShipment.ui.step - 1),
        error: null,
      });

    case types.CREATE_SHIPMENT_PROCEED_TO_SEND_INVOICE_REQUEST:
      return dot.merge(state, 'createShipment.ui', {
        isSendInvoiceFetching: true,
      });

    case types.CREATE_SHIPMENT_PROCEED_TO_SEND_INVOICE_SUCCESS: {
      let nextState = dot.set(
        state,
        'createShipment.emailTemplates',
        payload.emailTemplates,
      );
      nextState = dot.set(
        nextState,
        'createShipment.emailNotificationSettings',
        payload.emailNotificationSettings,
      );
      nextState = dot.merge(nextState, 'createShipment.ui', {
        error: null,
        step: 3,
        isSendInvoiceFetching: false,
      });
      return nextState;
    }

    case types.CREATE_SHIPMENT_PROCEED_TO_SEND_INVOICE_ERROR:
      return dot.merge(state, 'createShipment.ui', {
        error: getErrorMessage(payload),
        errorSource: 'fetchEmailTemplates',
        step: 3,
        isSendInvoiceFetching: false,
      });

    case types.CREATE_SHIPMENT_REQUEST:
      return dot.merge(state, 'createShipment.ui', {
        error: null,
        isCreating: true,
      });

    case types.CREATE_SHIPMENT_SUCCESS:
      // TODO: add new shipment to store
      return dot.merge(state, 'createShipment.ui', {
        isCreating: false,
      });

    case types.CREATE_SHIPMENT_SEND_INVOICE_SUCCESS:
      return dot.merge(state, 'createShipment.ui', {
        isModalOpen: false,
      });

    case types.CREATE_SHIPMENT_SEND_INVOICE_ERROR:
    case types.CREATE_SHIPMENT_ERROR:
      return dot.merge(state, 'createShipment.ui', {
        error: getErrorMessage(payload),
        errorSource: 'submitForm',
        isCreating: false,
      });

    case types.ORDER_COPY_REQUEST:
      return { ...state, isOrderQuoteCopying: true };

    case types.ORDER_COPY_ERROR:
    case types.ORDER_COPY_SUCCESS:
      return { ...state, isOrderQuoteCopying: false };

    case types.DOWNLOAD_FILES_REQUEST:
      return dot.merge(state, 'downloadFiles', {
        isFetching: true,
      });
    case types.DOWNLOAD_FILES_SUCCESS:
    case types.DOWNLOAD_FILES_ERROR:
      return dot.merge(state, 'downloadFiles', {
        isFetching: false,
      });

    case types.EXPORT_INVOICE_CSV_REQUEST:
      return {
        ...state,
        isExportingInvoicesToCsv: true,
      };
    case types.EXPORT_INVOICE_CSV_ERROR:
    case types.EXPORT_INVOICE_CSV_SUCCESS:
      return {
        ...state,
        isExportingInvoicesToCsv: false,
      };

    case types.CANCEL_ORDER_REQUEST:
      return dot.set(state, 'cancelOrder.isFetching', true);

    case types.CANCEL_ORDER_SUCCESS:
    case types.CANCEL_ORDER_ERROR:
      return dot.set(state, 'cancelOrder.isFetching', false);

    case types.REFUND_NOTIFICATION_OPEN_MODAL:
      return dot.merge(state, 'refundNotification', {
        isModalOpen: true,
        emailTemplates: payload.templates,
      });

    case types.REFUND_NOTIFICATION_CLOSE:
      return dot.merge(state, 'refundNotification', {
        isModalOpen: false,
      });

    case types.REFUND_NOTIFICATION_REQUEST:
      return dot.merge(state, 'refundNotification', {
        isFetching: true,
        error: null,
      });

    case types.REFUND_NOTIFICATION_SUCCESS:
      return dot.merge(state, 'refundNotification', {
        isFetching: false,
        isModalOpen: false,
      });

    case types.REFUND_NOTIFICATION_ERROR:
      return dot.merge(state, 'refundNotification', {
        isFetching: false,
        error: getErrorMessage(payload),
      });

    case types.UPDATE_PO_REQUEST:
      return { ...state, isPOUpdating: true };

    case types.UPDATE_PO_SUCCESS:
      return {
        ...state,
        isPOUpdating: false,
      };

    case types.UPDATE_PO_ERROR:
      return {
        ...state,
        isPOUpdating: false,
      };
    default:
      return state;
  }
};

export default reducer;
